import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'antd';
import { FileExcelOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useFeedback } from '../../../utils/feedback';
import { MARGIN_BUCKET_THRESHOLDS } from '../profitabilityAnalysis/analysis';
import { forecastMonthsLabel } from '../incomeStatement/incomeStatement';
import { exportSalesPivot } from '../incomeStatement/incomeStatementApi';
import { Callout, DashCard, money } from './DashboardCharts';
import {
  breakdownOrders,
  breakdownOrdersTotal,
  buildBreakdownExport,
  buildFigureBreakdown,
  figureMonths,
  type DashboardFigure,
} from './breakdownModel';
import { dk, type DashboardViewContext } from './viewContext';
import { CostPartialMark } from '../ReportTab';

interface Props {
  ctx: DashboardViewContext;
  figure: DashboardFigure;
}

/** Rows shown before „Pokaż wszystkie”; „Razem” always sums the whole list. */
const FIRST_ROWS = 8;

// Margin colour by the Analiza bands (MARGIN_BUCKET_THRESHOLDS), display only.
// A margin of a known part of the cost is not judged (no tone, only „*”).
function marginTone(margin: number | null, revenue: number, costPending = false): string {
  if (margin == null || !(revenue > 0) || costPending) return '';
  const value = (margin / revenue) * 100;
  if (value < 0) return ' is-margin-negative';
  if (value < MARGIN_BUCKET_THRESHOLDS.low) return ' is-margin-low';
  if (value < MARGIN_BUCKET_THRESHOLDS.medium) return ' is-margin-medium';
  return ' is-margin-good';
}

// Parts whose label starts with „−” (deductions from the tile).
const DEDUCTIONS = new Set(['soldGoods', 'fixedCosts', 'belowEbitda', 'incomeTax', 'awaitingGoods']);
const partAmount = (key: string, value: number) => {
  if (!DEDUCTIONS.has(key)) return money(value);
  return value > 0.5 ? `+ ${money(value)}` : money(Math.abs(value));
};

const percentText = (margin: number | null, revenue: number) => (
  margin == null || !(revenue > 0) ? '—' : `${((margin / revenue) * 100).toFixed(1).replace('.', ',')}%`
);

// "Skąd ta liczba": the parts of the selected Pulpit tile and, right under
// them, the orders behind it (profit at sale, 2026-10-29). Since PR2 the list
// is open from the start; its „Razem” equals the tile's part to the złoty and
// the list goes to Excel the same way as „Przekrój sprzedaży”.
const FigureBreakdown: React.FC<Props> = ({ ctx, figure }) => {
  const { t, language, statement } = ctx;
  const feedback = useFeedback();
  const [showAll, setShowAll] = useState(false);
  const [exporting, setExporting] = useState(false);
  const breakdown = useMemo(() => buildFigureBreakdown(figure, ctx.periodMonths), [figure, ctx.periodMonths]);
  const months = useMemo(() => figureMonths(figure, ctx.periodMonths), [figure, ctx.periodMonths]);
  const list = breakdown.defaultList === 'sold' ? 'sold' : 'waitingInPeriod';
  const rows = useMemo(() => breakdownOrders(list, months, statement), [list, months, statement]);
  // „Razem” sums every row (a ZK netting to ~0 is not listed but still counted).
  const total = useMemo(() => {
    const all = breakdownOrders(list, months, statement, true);
    return all ? breakdownOrdersTotal(all) : null;
  }, [list, months, statement]);
  const max = Math.max(1, ...breakdown.parts.map((part) => Math.abs(part.value)), Math.abs(breakdown.total));
  const b = (key: string, options?: Record<string, unknown>) => dk(t, `breakdown.${key}`, options);
  const orderLabel = (number: string) => dk(t, 'orderLabel', { number });
  const figureLabel = dk(t, `kpi.${figure}`);
  const visible = rows ? (showAll ? rows : rows.slice(0, FIRST_ROWS)) : [];
  // A quarter or a month carries its part of the year-to-date tax (counted from January).
  const partLabel = (key: string) => (key === 'incomeTax' && !ctx.wholeYear ? 'incomeTaxPart' : key);

  const runExport = async () => {
    if (!rows || !total) return;
    setExporting(true);
    try {
      const table = buildBreakdownExport(rows, total, {
        title: b('exportTitle', { figure: figureLabel, period: ctx.periodLabel }),
        sheet: b('exportSheet', { period: ctx.periodLabel }),
        info: [b(`hints.${list}`), ...(total.costPending ? [b('costPartial')] : [])],
        order: b('colOrder'),
        customer: b('colCustomer'),
        revenue: list === 'sold' ? b('colInvoiced') : b('colWaiting'),
        cost: b('colCost'),
        margin: b('colMargin'),
        marginPercent: b('colMarginPercent'),
        months: b('colMonths'),
        total: b('totalRow', { count: rows.length }),
        orderLabel,
        monthsLabel: (list2) => forecastMonthsLabel(list2, language),
      });
      const file = await exportSalesPivot(table);
      const url = window.URL.createObjectURL(file.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(b('exported', { filename: file.filename }));
    } catch {
      feedback.error(b('exportFailed'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <DashCard
      title={b('title', { figure: figureLabel })}
      icon={<QuestionCircleOutlined />}
      tone="indigo"
      lead={b('lead', { period: ctx.periodLabel })}
      extra={rows && rows.length > 0 ? (
        <Button size="small" icon={<FileExcelOutlined />} loading={exporting} onClick={runExport} data-testid="mgmt-breakdown-export">
          {b('export')}
        </Button>
      ) : undefined}
      className="mgmt-breakdown-card"
    >
      <div className="mgmt-breakdown" data-testid="mgmt-breakdown">
        {breakdown.parts.map((part) => (
          <div key={part.key} className="mgmt-breakdown__row">
            <span className="mgmt-breakdown__label" title={b(`parts.${partLabel(part.key)}`)}>{b(`parts.${partLabel(part.key)}`)}</span>
            {/* The label carries the „−” of a deduction; the amount is shown without
                a second sign (a deduction that turned positive, e.g. a tax refund, says „+”). */}
            <span className={`mgmt-breakdown__value${part.value < 0 ? ' is-deduction' : ''}`}>
              {partAmount(part.key, part.value)}
              {part.incomplete && <CostPartialMark hint={b('costPartial')} testId="mgmt-breakdown-part-partial" />}
            </span>
            <span className="mgmt-breakdown__track">
              <i className={part.value < 0 ? 'is-negative' : ''} style={{ width: `${Math.max(0.5, (Math.abs(part.value) / max) * 100)}%` }} />
            </span>
          </div>
        ))}
        <div className="mgmt-breakdown__row is-total">
          <span className="mgmt-breakdown__label">{b(`total.${figure}`)}</span>
          <span className={`mgmt-breakdown__value${breakdown.total < 0 ? ' is-negative' : ''}`}>
            {money(breakdown.total)}
            {breakdown.incomplete && <CostPartialMark hint={b('costPartial')} testId="mgmt-breakdown-total-partial" />}
          </span>
          <span />
        </div>
      </div>
      {figure !== 'revenueActual' && figure !== 'profitActual' && ctx.scenario !== 'base' && <Callout>{b('baseScenarioOnly')}</Callout>}
      <div className="mgmt-breakdown__list" data-testid="mgmt-breakdown-list">
        <h4 title={b(`hints.${list}`)}>{b(`lists.${list}`)}</h4>
        {rows == null ? <p className="mgmt-empty">{b('adminOnly')}</p> : rows.length === 0 ? (
          <p className="mgmt-empty">{b('noOrders')}</p>
        ) : (
          <div className="mgmt-table-wrap">
            <table className="mgmt-table mgmt-breakdown__table">
              <colgroup>
                <col className="mgmt-breakdown__col-order" />
                <col />
                <col className="mgmt-breakdown__col-money" />
                <col className="mgmt-breakdown__col-money" />
                <col className="mgmt-breakdown__col-money" />
                <col className="mgmt-breakdown__col-percent" />
                <col className="mgmt-breakdown__col-months" />
              </colgroup>
              <thead>
                <tr>
                  <th>{b('colOrder')}</th>
                  <th>{b('colCustomer')}</th>
                  <th className="is-num">{list === 'sold' ? b('colInvoiced') : b('colWaiting')}</th>
                  <th className="is-num">{b('colCost')}</th>
                  <th className="is-num">{b('colMargin')}</th>
                  <th className="is-num">{b('colMarginPercent')}</th>
                  <th>{b('colMonths')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr key={row.id} data-testid="mgmt-breakdown-order">
                    <td><Link className="report-cut" to={`/app/orders/${row.id}`} title={orderLabel(row.displayOrderNumber)}>{orderLabel(row.displayOrderNumber)}</Link></td>
                    <td><span className="report-cut" title={row.customerName}>{row.customerName}</span></td>
                    <td className="is-num">{money(row.revenue)}</td>
                    <td className="is-num">
                      {row.cost == null ? b('unknown') : money(row.cost)}
                      {row.cost != null && row.costPending && <CostPartialMark hint={b('costPartial')} testId="mgmt-breakdown-cost-partial" />}
                    </td>
                    <td className={`is-num${marginTone(row.margin, row.revenue, row.costPending)}`}>
                      {money(row.margin)}
                      {row.margin != null && row.costPending && <CostPartialMark hint={b('costPartial')} testId="mgmt-breakdown-margin-partial" />}
                    </td>
                    <td className={`is-num${marginTone(row.margin, row.revenue, row.costPending)}`}>
                      {percentText(row.margin, row.revenue)}
                      {row.margin != null && row.costPending && <CostPartialMark hint={b('percentPartial')} testId="mgmt-breakdown-percent-partial" />}
                    </td>
                    <td><span className="report-cut" title={forecastMonthsLabel(row.months, language)}>{forecastMonthsLabel(row.months, language)}</span></td>
                  </tr>
                ))}
              </tbody>
              {total && (
                <tfoot>
                  <tr data-testid="mgmt-breakdown-total">
                    <td colSpan={2}>{b('totalRow', { count: rows.length })}</td>
                    <td className="is-num">{money(total.revenue)}</td>
                    <td className="is-num">
                      {money(total.cost)}
                      {total.costPending && total.cost != null && <CostPartialMark hint={b('costPartial')} testId="mgmt-breakdown-sum-partial" />}
                    </td>
                    <td className="is-num">
                      {money(total.margin)}
                      {total.costPending && total.margin != null && <CostPartialMark hint={b('costPartial')} testId="mgmt-breakdown-sum-partial" />}
                    </td>
                    <td className="is-num">
                      {percentText(total.margin, total.revenue)}
                      {total.costPending && total.margin != null && <CostPartialMark hint={b('percentPartial')} testId="mgmt-breakdown-sum-partial" />}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
        {rows && rows.length > FIRST_ROWS && (
          <div className="mgmt-breakdown__more">
            <Button size="small" type="link" onClick={() => setShowAll((value) => !value)}>
              {showAll ? b('showFirst', { count: FIRST_ROWS }) : b('showAll', { count: rows.length })}
            </Button>
          </div>
        )}
      </div>
    </DashCard>
  );
};

export default FigureBreakdown;
